<template>
  <AIVDialog
    v-model="internalVisible"
    :title="dialogTitle"
    height="85vh"
    :no-body-padding="false"
    :no-body-overflow="false"
  >
    <!-- Step Indicator -->
    <el-steps :active="currentStep" finish-status="success" align-center class="cst-steps">
      <el-step title="Client Info" />
      <el-step title="Goals & Routine" />
      <el-step title="Preferences" />
      <el-step title="Health & Safety" />
      <el-step title="Behaviors" />
      <el-step title="Supports" />
      <el-step title="Emergency" />
      <el-step title="Medications" />
      <el-step title="Responsible Parties" />
    </el-steps>

    <div class="cst-editor" v-if="cstData.id">
      <el-form :model="cstData" label-position="top" class="cst-form">

        <!-- Step 1: Client Information -->
        <div v-show="currentStep === 0">
          <h5 class="section-title">Client Information</h5>
          <div class="form-grid">
            <el-form-item label="First Name">
              <el-input v-model="cstData.client_first_name" />
            </el-form-item>
            <el-form-item label="Last Name">
              <el-input v-model="cstData.client_last_name" />
            </el-form-item>
            <el-form-item label="Goes By">
              <el-input v-model="cstData.goes_by" />
            </el-form-item>
            <el-form-item label="Date of Birth">
              <el-date-picker v-model="cstData.dob" type="date" value-format="yyyy-MM-dd" style="width:100%" />
            </el-form-item>
            <el-form-item label="Gender">
              <el-input v-model="cstData.gender" />
            </el-form-item>
          </div>
        </div>

        <!-- Step 2: Goals & Routine -->
        <div v-show="currentStep === 1">
          <h5 class="section-title">Goals & Routine</h5>
          <el-form-item label="Goals">
            <el-input v-model="cstData.goals" type="textarea" :rows="5" />
          </el-form-item>
          <el-form-item label="Daily Service Routine">
            <el-input v-model="cstData.daily_service_routine" type="textarea" :rows="5" />
          </el-form-item>
          <el-form-item label="Communication Preferences">
            <el-input v-model="cstData.preferences_communication" type="textarea" :rows="4" />
          </el-form-item>
        </div>

        <!-- Step 3: Preferences -->
        <div v-show="currentStep === 2">
          <h5 class="section-title">Preferences</h5>
          <el-form-item label="Likes">
            <el-input v-model="cstData.likes" type="textarea" :rows="5" />
          </el-form-item>
          <el-form-item label="Dislikes">
            <el-input v-model="cstData.dislikes" type="textarea" :rows="5" />
          </el-form-item>
          <el-form-item label="Non-Negotiable Routines">
            <el-input v-model="cstData.non_negotiable_routines" type="textarea" :rows="4" />
          </el-form-item>
        </div>

        <!-- Step 4: Health & Safety -->
        <div v-show="currentStep === 3">
          <h5 class="section-title">Health & Safety</h5>
          <div class="form-grid">
            <el-form-item label="DNR">
              <AIVSelect v-model="cstData.dnr" :options="dnrOptions" :searchable="false" placeholder="Select..." />
            </el-form-item>
            <el-form-item label="Dietary Needs">
              <el-input v-model="cstData.dietary_needs" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item label="Functional Limitations">
              <el-input v-model="cstData.functional_limitations" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item label="Medical Needs">
              <el-input v-model="cstData.medical_needs" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item label="Bathroom Needs">
              <el-input v-model="cstData.bathroom_needs" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item label="Transportation Needs">
              <el-input v-model="cstData.transportation_needs" type="textarea" :rows="3" />
            </el-form-item>
          </div>
        </div>

        <!-- Step 5: Behaviors -->
        <div v-show="currentStep === 4">
          <h5 class="section-title">Behaviors</h5>
          <el-form-item label="Behaviors">
            <el-input v-model="cstData.behaviors" type="textarea" :rows="5" />
          </el-form-item>
          <el-form-item label="Behavior Intervention">
            <el-input v-model="cstData.behavior_intervention" type="textarea" :rows="4" />
          </el-form-item>
          <div class="form-grid">
            <el-form-item label="BSP Reference">
              <el-input v-model="cstData.bsp_reference" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item label="Human Rights Restrictions">
              <el-input v-model="cstData.human_rights_restrictions" type="textarea" :rows="3" />
            </el-form-item>
          </div>
        </div>

        <!-- Step 6: Supports & Notes -->
        <div v-show="currentStep === 5">
          <h5 class="section-title">Supports & Notes</h5>
          <el-form-item label="Natural Supports">
            <el-input v-model="cstData.natural_supports" type="textarea" :rows="5" />
          </el-form-item>
          <el-form-item label="Notes">
            <el-input v-model="cstData.notes" type="textarea" :rows="4" />
          </el-form-item>
        </div>

        <!-- Step 7: Emergency Contacts -->
        <div v-show="currentStep === 6">
          <h5 class="section-title">
            Emergency Contacts
            <el-button type="primary" size="mini" @click="addEmergencyContact">
              <i class="fa fa-plus"></i> Add
            </el-button>
          </h5>
          <AIVTable :columns="emergencyContactColumns" :data="cstData.emergencyContacts || []" :modalTable="true">
            <template #cell-name="{ row }"><el-input v-model="row.name" size="mini" /></template>
            <template #cell-relationship="{ row }"><el-input v-model="row.relationship" size="mini" /></template>
            <template #cell-phone="{ row }"><el-input v-model="row.phone" size="mini" /></template>
            <template #cell-phone_alt="{ row }"><el-input v-model="row.phone_alt" size="mini" /></template>
            <template #cell-actions="{ row }">
              <el-button type="danger" size="mini" icon="fa fa-trash" @click="removeRow(cstData.emergencyContacts, row)" />
            </template>
          </AIVTable>
        </div>

        <!-- Step 8: Medications -->
        <div v-show="currentStep === 7">
          <h5 class="section-title">
            Medications
            <el-button type="primary" size="mini" @click="addCstMedication">
              <i class="fa fa-plus"></i> Add
            </el-button>
          </h5>
          <AIVTable :columns="medicationColumns" :data="cstData.medications || []" :modalTable="true">
            <template #cell-name="{ row }"><el-input v-model="row.name" size="mini" /></template>
            <template #cell-dosage="{ row }"><el-input v-model="row.dosage" size="mini" /></template>
            <template #cell-frequency="{ row }"><el-input v-model="row.frequency" size="mini" /></template>
            <template #cell-route="{ row }"><el-input v-model="row.route" size="mini" /></template>
            <template #cell-instructions="{ row }"><el-input v-model="row.instructions" size="mini" /></template>
            <template #cell-actions="{ row }">
              <el-button type="danger" size="mini" icon="fa fa-trash" @click="removeRow(cstData.medications, row)" />
            </template>
          </AIVTable>
        </div>

        <!-- Step 9: Responsible Parties -->
        <div v-show="currentStep === 8">
          <h5 class="section-title">
            Responsible Parties
            <el-button type="primary" size="mini" @click="addResponsibleParty">
              <i class="fa fa-plus"></i> Add
            </el-button>
          </h5>
          <AIVTable :columns="responsiblePartyColumns" :data="cstData.responsibleParties || []" :modalTable="true">
            <template #cell-name="{ row }"><el-input v-model="row.name" size="mini" /></template>
            <template #cell-relationship="{ row }"><el-input v-model="row.relationship" size="mini" /></template>
            <template #cell-phone="{ row }"><el-input v-model="row.phone" size="mini" /></template>
            <template #cell-email="{ row }"><el-input v-model="row.email" size="mini" /></template>
            <template #cell-actions="{ row }">
              <el-button type="danger" size="mini" icon="fa fa-trash" @click="removeRow(cstData.responsibleParties, row)" />
            </template>
          </AIVTable>
        </div>

      </el-form>
    </div>

    <template slot="footer">
      <el-button v-if="currentStep > 0" @click="prevStep">
        <i class="fa fa-arrow-left"></i> Previous
      </el-button>
      <el-button v-if="currentStep < 8" type="primary" @click="nextStep">
        Next <i class="fa fa-arrow-right"></i>
      </el-button>
      <el-button v-if="cstData.status === 'draft'" type="success" size="small" @click="publishCst">
        <i class="fa fa-check-circle"></i> Publish
      </el-button>
      <el-button type="info" size="small" @click="saveCst" :disabled="!cstData.id">
        <i class="fa fa-save"></i> Save
      </el-button>
    </template>
  </AIVDialog>
</template>

<script>
import { cstService } from "@/serviceHandlers/cstService";

export default {
  name: "CstEditor",
  props: {
    visible: { type: Boolean, default: false },
    cstId: { type: [Number, String], default: null },
  },
  data() {
    return {
      cstData: {},
      currentStep: 0,
      dnrOptions: [
        { id: "yes", name: "Yes" },
        { id: "no", name: "No" },
      ],
      emergencyContactColumns: [
        { key: "name", label: "Name", sortable: false },
        { key: "relationship", label: "Relationship", sortable: false },
        { key: "phone", label: "Phone", sortable: false },
        { key: "phone_alt", label: "Alt Phone", sortable: false },
        { key: "actions", label: "", sortable: false },
      ],
      medicationColumns: [
        { key: "name", label: "Name", sortable: false },
        { key: "dosage", label: "Dosage", sortable: false },
        { key: "frequency", label: "Frequency", sortable: false },
        { key: "route", label: "Route", sortable: false },
        { key: "instructions", label: "Instructions", sortable: false },
        { key: "actions", label: "", sortable: false },
      ],
      responsiblePartyColumns: [
        { key: "name", label: "Name", sortable: false },
        { key: "relationship", label: "Relationship", sortable: false },
        { key: "phone", label: "Phone", sortable: false },
        { key: "email", label: "Email", sortable: false },
        { key: "actions", label: "", sortable: false },
      ],
    };
  },
  computed: {
    internalVisible: {
      get() { return this.visible; },
      set(val) {
        this.$emit('update:visible', val);
        if (!val) this.$emit('close');
      },
    },
    dialogTitle() {
      if (!this.cstData.id) return 'CST Editor';
      const name = [this.cstData.client_first_name, this.cstData.client_last_name].filter(Boolean).join(' ');
      const status = this.cstData.status ? ` — ${this.cstData.status} (v${this.cstData.version})` : '';
      return `Edit CST${name ? ' - ' + name : ''}${status}`;
    },
    statusTagType() {
      const map = { draft: "warning", active: "success", superseded: "info" };
      return map[this.cstData.status] || "info";
    },
  },
  watch: {
    cstId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.loadCst(id);
        } else {
          this.cstData = {};
        }
      },
    },
    visible(val) {
      if (!val) this.currentStep = 0;
    },
  },
  methods: {
    async loadCst(id) {
      this.$spinner.show();
      try {
        this.cstData = await cstService.getFull(id);
      } catch (error) {
        this.$notifyError("Could not load CST: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    nextStep() {
      if (this.currentStep >= 8) return;
      this.currentStep++;
      this.$nextTick(() => {
        const body = this.$el.querySelector('.cst-editor');
        if (body) body.scrollTop = 0;
      });
    },
    prevStep() {
      if (this.currentStep <= 0) return;
      this.currentStep--;
      this.$nextTick(() => {
        const body = this.$el.querySelector('.cst-editor');
        if (body) body.scrollTop = 0;
      });
    },
    async saveCst() {
      this.$spinner.show();
      try {
        await cstService.update(this.cstData.id, this.cstData);
        this.$notifySuccess("CST saved");
        this.$emit('saved');
      } catch (error) {
        this.$notifyError("Error saving CST: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    async publishCst() {
      try {
        await this.$confirm("Publishing will save and make this CST active, superseding any previous CST. Continue?", "Publish CST", {
          type: "warning",
        });
      } catch { return; }

      this.$spinner.show();
      try {
        // Save form data first, then publish
        await cstService.update(this.cstData.id, this.cstData);
        const result = await cstService.publish(this.cstData.id);
        this.cstData = result;
        this.$notifySuccess("CST saved and published successfully");
        this.$emit('saved');
      } catch (error) {
        this.$notifyError("Error publishing CST: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    removeRow(arr, row) {
      const idx = arr.indexOf(row);
      if (idx !== -1) arr.splice(idx, 1);
    },
    addEmergencyContact() {
      if (!this.cstData.emergencyContacts) this.$set(this.cstData, "emergencyContacts", []);
      this.cstData.emergencyContacts.push({ name: "", relationship: "", phone: "", phone_alt: "" });
    },
    addCstMedication() {
      if (!this.cstData.medications) this.$set(this.cstData, "medications", []);
      this.cstData.medications.push({ name: "", dosage: "", frequency: "", route: "", instructions: "" });
    },
    addResponsibleParty() {
      if (!this.cstData.responsibleParties) this.$set(this.cstData, "responsibleParties", []);
      this.cstData.responsibleParties.push({ name: "", relationship: "", phone: "", email: "" });
    },
  },
};
</script>

<style scoped>
.cst-steps {
  padding: 15px 0;
}
/* Active step: blue text and border so it's visible on dark background */
.cst-steps :deep(.el-step__head.is-process) {
  color: #409eff;
  border-color: #409eff;
}
.cst-steps :deep(.el-step__title.is-process) {
  color: #409eff;
}
.cst-steps :deep(.el-step__description.is-process) {
  color: #409eff;
}
.cst-editor {
  overflow-y: auto;
  max-height: calc(85vh - 180px);
  padding: 0 0.5rem 0.5rem;
}
.section-title {
  color: #fff;
  margin: 0.5rem 0 0.75rem;
  padding-bottom: 8px;
  border-bottom: 1px solid #444;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0 16px;
}
.cst-form :deep(.el-form-item__label) {
  color: #ffffff;
  font-weight: 500;
  padding-bottom: 2px;
  line-height: 1.4;
}
.cst-form :deep(.el-form-item) {
  margin-bottom: 14px;
}
</style>
